import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Calculator, FileArrowDown, Trash, GearSix, X, Info, UploadSimple, TreeStructure } from "@phosphor-icons/react";

const money = (v) => `€${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const FREQ = [{ v: "monthly", l: "Monthly" }, { v: "fortnightly", l: "Fortnightly" }, { v: "weekly", l: "Weekly" }];
const STATUS = [{ v: "director", l: "Proprietary director (PRSI Class S)" }, { v: "employee", l: "Employee (PRSI Class A)" }];

const NumInput = ({ label, value, onChange }) => (
  <div><label>{label}</label><input type="number" step="0.001" value={value} onChange={(e) => onChange(e.target.value)} /></div>
);

export default function Payroll() {
  const { user } = useAuth();
  const [f, setF] = useState({ employee_name: "", gross_per_period: "", frequency: "monthly", pp_status: "director", annual_tax_credits: "", annual_srcop: "", pay_date: new Date().toISOString().slice(0, 10), grant_id: "", grant_allocation: "" });
  const [slip, setSlip] = useState(null);
  const [history, setHistory] = useState([]);
  const [grants, setGrants] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const isOwner = user?.role === "owner";

  const loadHistory = useCallback(async () => {
    const [h, s] = await Promise.all([api.get("/payroll/payslips"), api.get("/payroll/summary")]);
    setHistory(h.data.items || []); setSummary(s.data);
  }, []);
  useEffect(() => { loadHistory(); api.get("/grants").then((r) => setGrants(r.data || [])).catch(() => {}); }, [loadHistory]);

  useEffect(() => {
    if (!user?.name) return;
    setF((p) => (p.employee_name ? p : { ...p, employee_name: user.name }));
  }, [user]);

  const run = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        employee_name: f.employee_name, gross_per_period: Number(f.gross_per_period),
        frequency: f.frequency, pp_status: f.pp_status, pay_date: f.pay_date,
      };
      if (f.annual_tax_credits !== "") payload.annual_tax_credits = Number(f.annual_tax_credits);
      if (f.annual_srcop !== "") payload.annual_srcop = Number(f.annual_srcop);
      if (f.grant_id) { payload.grant_id = f.grant_id; if (f.grant_allocation !== "") payload.grant_allocation = Number(f.grant_allocation); }
      const r = await api.post("/payroll/payslip", payload);
      setSlip(r.data);
      loadHistory();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not generate payslip"); }
    finally { setBusy(false); }
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const b64 = await new Promise((res, rej) => { const rd = new FileReader(); rd.onload = () => res(String(rd.result).split(",")[1]); rd.onerror = rej; rd.readAsDataURL(file); });
      const payload = { file_base64: b64, frequency: f.frequency };
      if (f.grant_id) { payload.grant_id = f.grant_id; if (f.grant_allocation !== "") payload.grant_allocation = Number(f.grant_allocation); }
      const r = await api.post("/payroll/import", payload);
      setSlip(r.data);
      loadHistory();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const downloadPdf = async (id) => {
    const r = await api.get(`/payroll/payslips/${id}.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `payslip-${id.slice(0, 8)}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };
  const del = async (id) => { if (!window.confirm("Delete this payslip?")) return; await api.delete(`/payroll/payslips/${id}`); loadHistory(); };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Payroll</div>
          <h1 className="font-display text-4xl">Payslip generator</h1>
          <p className="text-white/60 text-sm mt-2">Pay yourself or an employee — estimate PAYE, USC and PRSI and generate a payslip PDF. Indicative; operate real payroll through ROS.</p>
        </div>
        {isOwner && <button onClick={() => setConfigOpen(true)} className="btn-ghost text-sm" data-testid="btn-payroll-config"><GearSix size={16} /> Edit payroll rules</button>}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={run} className="glass rounded-2xl p-6 space-y-4">
          <div><label>Employee / director name</label><input required data-testid="pay-name" value={f.employee_name} onChange={(e) => set("employee_name", e.target.value)} /></div>
          <div><label>Status</label>
            <select data-testid="pay-status" value={f.pp_status} onChange={(e) => set("pp_status", e.target.value)}>
              {STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label>Gross per period (€)</label><input required type="number" step="0.01" data-testid="pay-gross" value={f.gross_per_period} onChange={(e) => set("gross_per_period", e.target.value)} /></div>
            <div><label>Frequency</label>
              <select data-testid="pay-frequency" value={f.frequency} onChange={(e) => set("frequency", e.target.value)}>
                {FREQ.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
              </select>
            </div>
          </div>
          <div><label>Pay date</label><input type="date" data-testid="pay-date" value={f.pay_date} onChange={(e) => set("pay_date", e.target.value)} /></div>
          <details className="text-sm text-white/60">
            <summary className="cursor-pointer">Override tax credits / cut-off (from your Revenue RPN)</summary>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div><label>Annual tax credits (€)</label><input type="number" step="0.01" placeholder="default" data-testid="pay-credits" value={f.annual_tax_credits} onChange={(e) => set("annual_tax_credits", e.target.value)} /></div>
              <div><label>Annual cut-off SRCOP (€)</label><input type="number" step="0.01" placeholder="default" data-testid="pay-srcop" value={f.annual_srcop} onChange={(e) => set("annual_srcop", e.target.value)} /></div>
            </div>
          </details>

          <div className="rounded-xl bg-black/25 border border-white/5 p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm text-white/70"><TreeStructure size={15} className="text-[#FFB020]" /> Allocate this cost to a grant (optional)</div>
            <div className="grid grid-cols-2 gap-4">
              <select data-testid="pay-grant" value={f.grant_id} onChange={(e) => set("grant_id", e.target.value)}>
                <option value="">Don&apos;t link</option>
                {grants.map((g) => <option key={g.grant_id} value={g.grant_id}>{g.name}</option>)}
              </select>
              <input type="number" step="0.01" placeholder="Allocation € (default: full cost)" data-testid="pay-grant-allocation" value={f.grant_allocation} onChange={(e) => set("grant_allocation", e.target.value)} disabled={!f.grant_id} />
            </div>
            {f.grant_id ? <div className="text-[11px] text-white/40">Draws down the grant&apos;s Personnel budget line.</div> : null}
          </div>

          <div className="flex gap-3">
            <button type="submit" disabled={busy} className="btn-gold flex-1 justify-center" data-testid="pay-calculate"><Calculator size={16} weight="fill" /> {busy ? "Calculating…" : "Generate payslip"}</button>
            <label className="btn-ghost cursor-pointer" data-testid="pay-import-label">
              <UploadSimple size={16} /> {importing ? "Reading…" : "Import (Sage…)"}
              <input type="file" accept="image/*,.pdf" className="hidden" onChange={importFile} data-testid="pay-import-input" disabled={importing} />
            </label>
          </div>
          <p className="text-[11px] text-white/40">Already run payroll in Sage/BrightPay? Import the payslip PDF — we&apos;ll read it and record the same cost (and link it to your grant if selected).</p>
        </form>

        {slip ? <PayslipResult slip={slip} onPdf={() => downloadPdf(slip.payslip_id)} /> : (
          <div className="glass rounded-2xl p-6 flex items-center justify-center text-white/40 text-sm text-center">Enter pay details to generate a payslip.</div>
        )}
      </div>

      {/* Company-wide payroll summary */}
      {summary && summary.count > 0 && (
        <div className="glass rounded-2xl p-6" data-testid="payroll-summary">
          <div className="eyebrow mb-4">Company-wide payroll (for financial statements & runway)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Total employer cost</div><div className="mono text-2xl mt-1">{money(summary.total_employer_cost)}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Gross pay</div><div className="mono text-2xl mt-1">{money(summary.total_gross)}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Grant-funded</div><div className="mono text-2xl mt-1 text-[#00A86B]">{money(summary.total_grant_funded)}</div></div>
            <div><div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Company-funded</div><div className="mono text-2xl mt-1">{money(summary.company_funded)}</div></div>
          </div>
          <div className="text-[11px] text-white/40 mt-3">{summary.count} payslip{summary.count === 1 ? "" : "s"} recorded · feeds the CRO financial-statements staff-costs note.</div>
        </div>
      )}

      {/* History */}
      <div className="glass rounded-2xl p-6" data-testid="payslip-history">
        <div className="eyebrow mb-4">Payslip history</div>
        {history.length === 0 ? (
          <div className="text-white/40 text-sm">No payslips yet.</div>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.payslip_id} className="flex items-center justify-between rounded-xl bg-black/25 border border-white/5 px-4 py-3" data-testid={`payslip-${h.payslip_id}`}>
                <div>
                  <div className="text-sm font-semibold">{h.inputs.employee_name} · {money(h.results.net_pay)} net</div>
                  <div className="text-[11px] text-white/45 mono">{(h.inputs.pay_date || h.timestamp.slice(0, 10))} · {h.inputs.frequency} · {h.results.prsi_class}</div>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => downloadPdf(h.payslip_id)} className="text-white/50 hover:text-[#FFB020]" data-testid={`pdf-${h.payslip_id}`}><FileArrowDown size={16} /></button>
                  <button onClick={() => del(h.payslip_id)} className="text-white/40 hover:text-[#E63946]" data-testid={`del-${h.payslip_id}`}><Trash size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>{configOpen && <PayrollConfigModal onClose={() => setConfigOpen(false)} />}</AnimatePresence>
    </div>
  );
}

function PayslipResult({ slip, onPdf }) {
  const r = slip.results;
  const rows = [
    ["Gross pay", r.gross, false],
    ["PAYE (Income Tax)", r.paye, true],
    ["USC", r.usc, true],
    [`PRSI (${r.prsi_class})`, r.prsi_employee, true],
  ];
  return (
    <div className="glass rounded-2xl p-6 space-y-4" data-testid="payslip-result">
      <div className="flex items-center justify-between">
        <div className="eyebrow">Payslip</div>
        <button onClick={onPdf} className="btn-gold text-sm" data-testid="pay-download-pdf"><FileArrowDown size={15} weight="fill" /> PDF</button>
      </div>
      <div className="rounded-xl bg-black/30 border border-white/5 divide-y divide-white/5">
        {rows.map(([label, val, ded]) => (
          <div key={label} className="flex items-center justify-between px-4 py-2.5 text-sm">
            <span className={ded ? "text-white/60" : "text-white/80"}>{label}</span>
            <span className="mono" style={{ color: ded ? "#E63946" : "#F3F5F4" }}>{ded ? "− " : ""}{money(val)}</span>
          </div>
        ))}
        <div className="flex items-center justify-between px-4 py-3 bg-[#00A86B]/10">
          <span className="font-semibold text-[#00A86B]">Net pay</span>
          <span className="mono text-xl text-[#00A86B]">{money(r.net_pay)}</span>
        </div>
      </div>
      <div className="text-xs text-white/50">Employer PRSI {money(r.employer_prsi)} · Total employer cost {money(r.employer_cost)} · Annualised {money(r.annualised)}</div>
      <div className="flex items-start gap-2 text-xs text-white/50 border-t border-white/5 pt-3"><Info size={14} className="mt-0.5 shrink-0" /> {slip.disclaimer}</div>
    </div>
  );
}

function PayrollConfigModal({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [msg, setMsg] = useState(null);
  useEffect(() => { api.get("/payroll/config").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return null;
  const setTax = (k, v) => setCfg((c) => ({ ...c, tax: { ...c.tax, [k]: v } }));
  const setCr = (k, v) => setCfg((c) => ({ ...c, credits: { ...c.credits, [k]: v } }));
  const setPr = (k, v) => setCfg((c) => ({ ...c, prsi: { ...c.prsi, [k]: v } }));

  const save = async () => {    setMsg(null);
    try {
      await api.put("/payroll/config", {
        tax: { standard_rate: Number(cfg.tax.standard_rate), higher_rate: Number(cfg.tax.higher_rate), srcop_single: Number(cfg.tax.srcop_single) },
        credits: { personal: Number(cfg.credits.personal), employee_paye: Number(cfg.credits.employee_paye), earned_income: Number(cfg.credits.earned_income) },
        usc: cfg.usc,
        prsi: { ...cfg.prsi, employee_class_a: Number(cfg.prsi.employee_class_a), class_s: Number(cfg.prsi.class_s), employer_class_a_lower: Number(cfg.prsi.employer_class_a_lower), employer_class_a_higher: Number(cfg.prsi.employer_class_a_higher), employer_lower_threshold_weekly: Number(cfg.prsi.employer_lower_threshold_weekly), class_s_annual_min: Number(cfg.prsi.class_s_annual_min) },
        year: Number(cfg.year),
      });
      setMsg({ ok: true, text: "Payroll rules updated." });
    } catch (err) { setMsg({ ok: false, text: err?.response?.data?.detail || "Save failed." }); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="payroll-config-modal">
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="glass rounded-2xl w-full max-w-2xl my-8 p-8">
        <div className="flex items-center justify-between mb-6">
          <div><div className="eyebrow mb-1">Admin</div><div className="font-display text-2xl">Edit payroll rules</div></div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="close-payroll-config"><X size={20} /></button>
        </div>
        <div className="space-y-6">
          <div>
            <div className="eyebrow mb-3">Income Tax</div>
            <div className="grid grid-cols-3 gap-4">
              <NumInput label="Standard rate (0-1)" value={cfg.tax.standard_rate} onChange={(v) => setTax("standard_rate", v)} />
              <NumInput label="Higher rate (0-1)" value={cfg.tax.higher_rate} onChange={(v) => setTax("higher_rate", v)} />
              <NumInput label="SRCOP single (€)" value={cfg.tax.srcop_single} onChange={(v) => setTax("srcop_single", v)} />
            </div>
          </div>
          <div>
            <div className="eyebrow mb-3">Tax credits (annual €)</div>
            <div className="grid grid-cols-3 gap-4">
              <NumInput label="Personal" value={cfg.credits.personal} onChange={(v) => setCr("personal", v)} />
              <NumInput label="Employee (PAYE)" value={cfg.credits.employee_paye} onChange={(v) => setCr("employee_paye", v)} />
              <NumInput label="Earned income" value={cfg.credits.earned_income} onChange={(v) => setCr("earned_income", v)} />
            </div>
          </div>
          <div>
            <div className="eyebrow mb-3">PRSI (rates 0-1)</div>
            <div className="grid grid-cols-3 gap-4">
              <NumInput label="Employee Class A" value={cfg.prsi.employee_class_a} onChange={(v) => setPr("employee_class_a", v)} />
              <NumInput label="Class S (director)" value={cfg.prsi.class_s} onChange={(v) => setPr("class_s", v)} />
              <NumInput label="Employer lower" value={cfg.prsi.employer_class_a_lower} onChange={(v) => setPr("employer_class_a_lower", v)} />
              <NumInput label="Employer higher" value={cfg.prsi.employer_class_a_higher} onChange={(v) => setPr("employer_class_a_higher", v)} />
              <NumInput label="Employer lower threshold (weekly €)" value={cfg.prsi.employer_lower_threshold_weekly} onChange={(v) => setPr("employer_lower_threshold_weekly", v)} />
              <NumInput label="Class S annual min (€)" value={cfg.prsi.class_s_annual_min} onChange={(v) => setPr("class_s_annual_min", v)} />
            </div>
          </div>
        </div>
        {msg ? <div className={`text-sm mt-4 ${msg.ok ? "text-[#00A86B]" : "text-[#E63946]"}`} data-testid="payroll-config-msg">{msg.text}</div> : null}
        <div className="flex justify-end gap-3 pt-5 mt-5 border-t border-white/5">
          <button onClick={onClose} className="btn-ghost">Close</button>
          <button onClick={save} className="btn-gold" data-testid="save-payroll-config">Save rules</button>
        </div>
      </motion.div>
    </motion.div>
  );
}
